/**
 * BlogCard Component
 * 
 * Displays a blog post preview with image, title, excerpt, and metadata.
 * 
 * Props:
 * - post: Object containing title, excerpt, author, date, image, etc.
 */

import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import ResponsiveImage from './ResponsiveImage'
import { teamBySlug } from '../../data/team'

const BlogCard = ({ post }) => {
  const author = teamBySlug(post.authorSlug)
  // Format date
  const formattedDate = new Date(`${post.date}T12:00:00`).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <motion.article 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="group flex flex-col bg-white rounded-2xl border border-dark-200/25 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
    >
      {/* Image */}
      <div className="aspect-[4/3] w-full overflow-hidden">
        <ResponsiveImage
          src={post.image}
          alt={post.title}
          sizes="(min-width: 1024px) 400px, (min-width: 768px) 50vw, 100vw"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>

      <div className="flex flex-col flex-grow p-6 md:p-8">
        {/* Category Badge */}
        <div className="mb-4">
          <span className="inline-block px-3 py-1 bg-cream-100 border border-dark-200/20 text-dark-500 text-[10px] uppercase tracking-[0.15em] font-sans font-medium rounded-full">
            {post.category}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-display text-xl md:text-2xl text-dark-700 font-medium mb-3 group-hover:text-brand-orange-500 transition-colors">
          <Link to={`/blog/${post.slug}`} className="focus:outline-none">
            {post.title}
          </Link>
        </h3>

        {/* Excerpt */}
        <p className="font-body text-dark-500 text-base leading-relaxed mb-6 line-clamp-3">
          {post.excerpt}
        </p>

        <div className="mt-auto pt-4 border-t border-dark-200/20 flex flex-wrap items-center justify-between gap-4 text-xs font-sans text-dark-400">
          <div className="flex items-center gap-2">
            <span className="font-medium text-dark-600">{author?.name}</span>
            <span>·</span>
            <span>{formattedDate}</span>
          </div>
          <span className="uppercase tracking-widest">{post.readTime}</span>
        </div>
      </div>
    </motion.article>
  )
}

export default BlogCard
